import React, { useState, useRef, useEffect } from 'react';
import ReactModal from '../../../shared/components/react_modal';
import CSVDownloader from './csv_downloader';
import Importer from './importer';
import ReactPaginate from 'react-paginate';
import BeatLoader from 'react-spinners/BeatLoader';
import { isEmpty, uniq, startCase, difference } from 'lodash';
import moment from 'moment';

import { fetchInviteMembers, sendEventInvites } from '~/../components/shared/utils/api';

const InviteGuests = ({ event, users, pageCount, allUserIds, eventInvites, alreadyInvited, customMessage }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [sendInvitesLoading, setSendInvitesLoading] = useState(false);
  const [guests, setGuests] = useState(users);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(pageCount);
  const [search, setSearch] = useState('');
  const [selectedGuests, setSelectedGuests] = useState([]);
  const [userIds, setUserIds] = useState(allUserIds);
  const [invitedList, setInvitedList] = useState(eventInvites);
  const [alreadyInvitedList, setAlreadyInvitedList] = useState(alreadyInvited);
  const [feedback, setFeedback] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTimerId, setModalTimerId] = useState(null);
  const feedbackTimer = useRef(null);

  useEffect(() => {
    return () => clearTimeout(feedbackTimer.current);
  }, []);

  useEffect(() => {
    clearTimeout(modalTimerId);
  }, [modalOpen]);

  useEffect(() => {
    if (feedback.type === 'download') {
      feedbackTimer.current = setTimeout(() => setFeedback({}), 7000);
    }
  }, [feedback])

  const handlePageClick = ({ selected }) => {
    const newPage = selected + 1;

    setIsLoading(true);
    setCurrentPage(selected);
    fetchInviteMembers(event.slug, newPage).then(resp => {
      handleFetchResponse(resp.data);
    });
  }

  const handleFetchResponse = ({ users, eventInvites, pageCount, allUserIds }) => {
    setInvitedList(eventInvites);
    setGuests(users);
    setTotalPages(pageCount);
    setUserIds(allUserIds);
    setIsLoading(false);
  }

  const handleTextSearch = (e) => {
    e.preventDefault();
    setSearch(e.target.value);
  };

  const handleKeyEnter = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setIsLoading(true);
      setCurrentPage(0);
      fetchInviteMembers(event.slug, 1, search).then(resp => {
        handleFetchResponse(resp.data);
      });
    }
  };

  const handleSelectGuest = (selected, guestId) => {
    if (!selected) {
      setSelectedGuests([...selectedGuests, guestId]);
    }
    else {
      setSelectedGuests(selectedGuests.filter(id => id !== guestId))
    }
  }

  const handleInviteAll = () => {
    const allSelected = uniq([...selectedGuests, ...userIds]);
    setSelectedGuests(difference(allSelected, alreadyInvited));
  };

  const handleSendAllInvites = () => {
    if (confirm(`Are you sure you want to invite ${selectedGuests.length} guest(s)?`)) {
      setSendInvitesLoading(true);
      const currentUserList = guests.map(guest => guest.id);

      sendEventInvites(event.slug, selectedGuests, currentUserList).then(resp => {
        setAlreadyInvitedList(resp.data.alreadyInvited);
        setInvitedList(resp.data.eventInvites);
        setSelectedGuests([]);
        setSendInvitesLoading(false);
        setFeedback({ msg: 'Successfully Invited', type: 'send-all'})
        feedbackTimer.current = setTimeout(() => setFeedback({}), 7000);
      });
    }
  };

  const buildInviteLabel = (invited, guest, selected) => {
    if (invited) {
      return { style: 'selected', text: startCase(invited.status) }
    }
    else {
      if (selected) {
        return { style: 'selected', text: 'Selected' }
      }
      else {
        return { style: '', text: 'Invite' }
      }
    }
  }

  const renderGuest = guest => {
    const invited = invitedList.find(invite => invite.recipientId === guest.id);
    const selected = selectedGuests.includes(guest.id);
    const label = buildInviteLabel(invited, guest, selected);

    return (
      <div className='card-container' key={guest.id}>
        <div className='voray-entity-card card-circle'>
          <div className='image'>
            <img src={guest.headshot} alt={guest.displayName} />
          </div>
          <div className='fw'>
            <div className='left'>
              <div className='align-center display-flex'>
                <span className='text-black t3 w3'>{guest.displayName}</span>
                <span className='badge text-uppercase'>{guest.badge}</span>
              </div>
              <span className='block'>{guest.companyTitle}</span>
              {invited &&
                <span className='block mt-5'>
                  Invite sent on {moment(invited.createdAt).format('l')}
                </span>
              }
            </div>
            <button
              className={`btn-main invite-btn right mt-15 ${label.style}`}
              onClick={invited ? () => {} : () => handleSelectGuest(selected, guest.id)}
            >
              {label.text}
            </button>
          </div>
        </div>
      </div>
    )
  }

  const renderGuests = () => {
    return (
      <div className='mt-20'>
        {isEmpty(guests) ? <span>No Guests Found</span> : guests?.map(guest => renderGuest(guest))}
      </div>
    );
  }

  const handleModalClose = () => setModalOpen(false);

  const handleModal = () => {
  return modalOpen ?
    <ReactModal title={'External Invites'} onClose={handleModalClose}>
      <Importer data={{ slug: event.slug, id: event.id }} setModalOpen={setModalOpen} setModalTimerId={setModalTimerId} customMessage={customMessage} />
    </ReactModal> : null
  }

  return (
    <>
      <header className='header pt-10 pb-10 h-70'>
        {handleModal()}
        <div className='container--extend'>
          <h3 className='ib left ml-20'>{event.title}</h3>
          <div className='right action-buttons'>
            {sendInvitesLoading &&
              <BeatLoader color='#3d898b' speedMultiplier={0.5} className='mr-15 left mt-10'/>}
            <button
              onClick={() => handleSendAllInvites()}
              className={`${(isEmpty(selectedGuests) || sendInvitesLoading) ? 'disabled' : ''}
                            btn-main btn-huge fs-14 mr-20`}
            >
              Send all invites
            </button>
            <a className='btn-main btn-huge fs-14 mr-20'
              href={`${location.origin}/events/${event.slug}`}>
                Done
            </a>
          </div>
        {!isEmpty(feedback) && feedback.type === 'send-all' && <span className='green mt-15 block mr-15 right'>{feedback.msg}</span>}
        </div>
      </header>
      <div className='container-fluid col-xs-8 no-float main-container mt-30'>
        <div className='manage-guests-container mb-40'>
          <div>
            <h3 className='ib mr-15'>Manage Guests</h3>
            <span className='subtitle'>{alreadyInvitedList.length} Members</span>
          </div>
          <div>
            <CSVDownloader slug={event.slug} setFeedback={setFeedback} />
            <button className='btn-main ml-15' onClick={() => setModalOpen(true)}>Import</button>
          </div>
        </div>
        {!isEmpty(feedback) && feedback.type === 'download' && <span className='danger-feedback'>{feedback.msg}</span>}

        <input
          className='form-control p-3 rounded-3 w-100'
          type='search'
          placeholder={'Search for a guest'}
          aria-label='Search'
          onChange={e => handleTextSearch(e)}
          onKeyDown={handleKeyEnter}
          value={search}
        />
        <div className='filters-container'>
          <button className={`${isEmpty(userIds) ? 'disabled' : ''} btn-main right`}
                  onClick={handleInviteAll}>Invite All</button>
        </div>
        {isLoading
          ? <BeatLoader color='#3d898b' speedMultiplier={0.5} className='mt-50 ml-15' />
          : renderGuests()
        }
        <div className='pagination-container mb-50'>
          <ReactPaginate
            breakLabel='...'
            nextLabel='next >'
            onPageChange={handlePageClick}
            pageRangeDisplayed={5}
            pageCount={totalPages}
            previousLabel='< previous'
            renderOnZeroPageCount={null}
            containerClassName='pagination'
            forcePage={currentPage}
          />
        </div>
      </div>
    </>
  );
};

export default InviteGuests;
